/**
 * StaffProfile360Page — consolidated 360° view of a single staff member.
 * Replaces the "click through 4 separate pages per staff" UX. Tabs:
 *   Ringkasan (KPI snapshot)
 *   Payroll & Kontrak
 *   Kehadiran
 *   Prestasi & KPI
 *   Latihan
 *   Cuti
 *   Disiplin & Ganjaran
 *   Kemahiran
 *   Dokumen
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, User, Phone, Mail, MapPin, Building2, Briefcase, Calendar,
  Award, GraduationCap, FileText, AlertTriangle, Gift, Sparkles, FileWarning,
  Target, Clock, UserCheck, DollarSign, Landmark, RefreshCw, CheckCircle2,
  CreditCard,
} from 'lucide-react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'summary', label: 'Ringkasan', icon: User },
  { id: 'payroll', label: 'Payroll & Kontrak', icon: DollarSign },
  { id: 'attendance', label: 'Kehadiran', icon: UserCheck },
  { id: 'performance', label: 'Prestasi & KPI', icon: Target },
  { id: 'training', label: 'Latihan', icon: GraduationCap },
  { id: 'leave', label: 'Cuti', icon: Calendar },
  { id: 'conduct', label: 'Disiplin & Ganjaran', icon: FileWarning },
  { id: 'skills', label: 'Kemahiran', icon: Sparkles },
  { id: 'documents', label: 'Dokumen', icon: FileText },
];

const InfoChip = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 text-sm">
    <Icon className="w-4 h-4 text-gray-400 shrink-0" />
    <span className="text-gray-500">{label}:</span>
    <span className="text-gray-900 font-medium truncate">{value || '—'}</span>
  </div>
);

const Stat = ({ label, value, sub, tone = 'gray', testid }) => {
  const tones = {
    gray: 'bg-gray-50',
    emerald: 'bg-emerald-50 text-emerald-800',
    red: 'bg-red-50 text-red-800',
    amber: 'bg-amber-50 text-amber-800',
    blue: 'bg-blue-50 text-blue-800',
    purple: 'bg-purple-50 text-purple-800',
  };
  return (
    <div className={`${tones[tone]} rounded-xl p-3 border border-gray-100`} data-testid={testid}>
      <p className="text-xs text-gray-600 font-medium">{label}</p>
      <p className="text-xl font-bold mt-0.5">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const s = (status || '').toLowerCase();
  const map = {
    hadir: 'bg-emerald-100 text-emerald-700',
    present: 'bg-emerald-100 text-emerald-700',
    wfh: 'bg-blue-100 text-blue-700',
    lewat: 'bg-amber-100 text-amber-700',
    late: 'bg-amber-100 text-amber-700',
    'tidak hadir': 'bg-red-100 text-red-700',
    absent: 'bg-red-100 text-red-700',
    cuti: 'bg-purple-100 text-purple-700',
    approved: 'bg-emerald-100 text-emerald-700',
    pending: 'bg-amber-100 text-amber-700',
    rejected: 'bg-red-100 text-red-700',
    active: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-emerald-100 text-emerald-700',
    in_progress: 'bg-blue-100 text-blue-700',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${map[s] || 'bg-gray-100 text-gray-600'}`}>
      {status || '—'}
    </span>
  );
};

const Section = ({ title, children, empty, testid }) => (
  <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm" data-testid={testid}>
    <h3 className="font-semibold text-gray-800 mb-3">{title}</h3>
    {empty ? <p className="text-sm text-gray-400 py-3 text-center">{empty}</p> : children}
  </div>
);

const SimpleTable = ({ columns, rows, emptyMsg = 'Tiada rekod', renderCell }) => {
  if (!rows?.length) {
    return <p className="text-sm text-gray-400 py-4 text-center">{emptyMsg}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
            {columns.map((c) => <th key={c.key} className="py-2 pr-4">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={r.id || idx} className="border-b border-gray-50 last:border-0">
              {columns.map((c) => (
                <td key={c.key} className="py-2 pr-4 text-gray-700">
                  {renderCell ? renderCell(c.key, r) : (r[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const StaffProfile360Page = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/staff/${staffId}/profile360`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Ralat');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData(null); // reset to avoid flashing previous staff's data
    fetchData();
    /* eslint-disable-next-line */
  }, [staffId]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="profile-loading">
        <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500">Memuatkan profil…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-red-700" data-testid="profile-error">
        <p className="font-semibold">Ralat</p>
        <p className="text-sm mt-1">{error}</p>
        <button onClick={() => navigate('/staff-payroll')} className="mt-3 underline text-sm">
          Kembali ke senarai staff
        </button>
      </div>
    );
  }
  if (!data) return null;

  const { staff, tenure_years, contact, documents, attendance, performance, training, leave, kpi, disciplinary, rewards, skills, contract } = data;

  // Attendance pie data
  const attendancePie = [
    { name: 'Hadir', value: attendance.present, color: '#10b981' },
    { name: 'Lewat', value: attendance.late, color: '#f59e0b' },
    { name: 'Tidak Hadir', value: attendance.absent, color: '#ef4444' },
    { name: 'Cuti', value: attendance.leave, color: '#8b5cf6' },
  ].filter((d) => d.value > 0);

  return (
    <div className="space-y-5" data-testid="staff-profile-360">
      <button
        onClick={() => navigate('/staff-payroll')}
        className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        data-testid="profile-back"
      >
        <ArrowLeft className="w-4 h-4" /> Senarai Staff
      </button>

      {/* Header card */}
      <div className="bg-gradient-to-r from-red-700 to-red-900 rounded-2xl p-5 sm:p-6 text-white" data-testid="profile-header">
        <div className="flex flex-col lg:flex-row lg:items-start gap-5 lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/20 flex items-center justify-center text-xl sm:text-2xl font-bold flex-shrink-0">
              {(staff.name || '?').split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'}
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold" data-testid="profile-name">{staff.name}</h1>
              <p className="text-red-200 text-sm">
                {staff.position || 'Tiada jawatan'}
                {staff.department && <> • {staff.department}</>}
              </p>
              <div className="flex flex-wrap gap-2 mt-2 text-xs">
                <span className="px-2 py-0.5 rounded-full bg-white/15">IC: {staff.ic_number || '—'}</span>
                <span className="px-2 py-0.5 rounded-full bg-white/15">Status: {staff.status || 'Active'}</span>
                {tenure_years !== null && <span className="px-2 py-0.5 rounded-full bg-white/15">Tenure: {tenure_years} thn</span>}
                {contract?.expiring_soon_60d && <span className="px-2 py-0.5 rounded-full bg-amber-400 text-amber-900 font-semibold">Kontrak tamat &lt; 60 hari</span>}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center min-w-fit">
            <div className="bg-white/10 rounded-lg px-3 py-2">
              <p className="text-xs text-red-200">Hadir</p>
              <p className="text-lg font-bold" data-testid="hero-attendance">{attendance.attendance_rate}%</p>
            </div>
            <div className="bg-white/10 rounded-lg px-3 py-2">
              <p className="text-xs text-red-200">Prestasi</p>
              <p className="text-lg font-bold" data-testid="hero-performance">{performance.avg_score}</p>
            </div>
            <div className="bg-white/10 rounded-lg px-3 py-2">
              <p className="text-xs text-red-200">KPI</p>
              <p className="text-lg font-bold" data-testid="hero-kpi">{kpi.avg_achievement}%</p>
            </div>
            <div className="bg-white/10 rounded-lg px-3 py-2">
              <p className="text-xs text-red-200">Latihan</p>
              <p className="text-lg font-bold" data-testid="hero-training">{training.completion_rate}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white rounded-xl p-1.5 border border-gray-100 shadow-sm overflow-x-auto" data-testid="profile-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`profile-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap flex-shrink-0 transition-all ${
                active ? 'bg-red-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* ================ Tab Content ================ */}
      {activeTab === 'summary' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="tab-summary">
          <Section title="Maklumat Hubungan">
            <div className="space-y-2">
              <InfoChip icon={Phone} label="No. Telefon" value={contact.phone} />
              <InfoChip icon={Mail} label="Emel" value={contact.email} />
              <InfoChip icon={MapPin} label="Alamat" value={contact.address} />
              <InfoChip icon={Landmark} label="Bank" value={contact.bank_name} />
              <InfoChip icon={CreditCard} label="Akaun" value={contact.bank_account} />
              <InfoChip icon={Building2} label="Jabatan" value={staff.department} />
              <InfoChip icon={Briefcase} label="Jawatan" value={staff.position} />
              <InfoChip icon={Calendar} label="Tarikh Masuk" value={staff.join_date} />
            </div>
          </Section>

          <Section title="Ringkasan Prestasi (3 Bulan)">
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Kadar Kehadiran" value={`${attendance.attendance_rate}%`} sub={`${attendance.total_records} rekod`} tone="emerald" testid="sum-attendance" />
              <Stat label="Ketepatan Masa" value={`${attendance.punctuality_rate}%`} sub={`${attendance.late} lewat`} tone="amber" testid="sum-punctuality" />
              <Stat label="Prestasi Purata" value={performance.avg_score} sub={`${performance.total_reviews} review`} tone="blue" testid="sum-perf" />
              <Stat label="KPI Pencapaian" value={`${kpi.avg_achievement}%`} sub={`${kpi.total} KPI`} tone="purple" testid="sum-kpi" />
              <Stat label="Latihan Selesai" value={`${training.completed}/${training.total}`} sub={`RM ${training.total_cost.toLocaleString()}`} tone="blue" testid="sum-training" />
              <Stat label="Hari Cuti" value={leave.approved_days_total} sub={`${leave.pending} menunggu`} tone="purple" testid="sum-leave" />
            </div>
          </Section>

          <Section title="Disiplin & Ganjaran">
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Tindakan Disiplin" value={disciplinary.total} tone={disciplinary.total > 0 ? 'red' : 'gray'} testid="sum-disciplinary" />
              <Stat label="Ganjaran" value={rewards.total} sub={`RM ${rewards.total_value.toLocaleString()}`} tone="emerald" testid="sum-rewards" />
            </div>
          </Section>

          <Section title="Kehadiran Visual">
            {attendancePie.length > 0 ? (
              <div style={{ width: '100%', height: 224 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={attendancePie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={75} paddingAngle={2}>
                      {attendancePie.map((d) => <Cell key={d.name} fill={d.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-10 text-center">Tiada data kehadiran 3 bulan terakhir</p>
            )}
          </Section>
        </div>
      )}

      {activeTab === 'payroll' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="tab-payroll">
          <Section title="Komponen Gaji">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Gaji Asas</span><span className="font-semibold">RM {(staff.basic_salary || 0).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Elaun</span><span className="font-semibold">RM {(staff.allowance || 0).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">OT ({staff.overtime_hours || 0} jam)</span><span className="font-semibold">RM {(staff.overtime_pay || 0).toLocaleString()}</span></div>
              <div className="flex justify-between border-t border-gray-100 pt-2"><span className="text-gray-700 font-medium">Gaji Kasar</span><span className="font-bold">RM {(staff.gross_salary || 0).toLocaleString()}</span></div>
            </div>
          </Section>

          <Section title="Potongan Berkanun">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">EPF (Pekerja)</span><span>RM {(staff.epf_employee || 0).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">SOCSO (Pekerja)</span><span>RM {(staff.socso_employee || 0).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">EIS (Pekerja)</span><span>RM {(staff.eis_employee || 0).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">PCB</span><span>RM {(staff.pcb_amount || 0).toFixed(2)}</span></div>
              <div className="flex justify-between border-t border-gray-100 pt-2"><span className="text-gray-700 font-medium">Jumlah Potongan</span><span className="font-bold text-red-600">RM {(staff.total_deductions || 0).toFixed(2)}</span></div>
              <div className="flex justify-between bg-emerald-50 -mx-2 px-2 py-2 rounded"><span className="font-semibold text-emerald-800">Gaji Bersih</span><span className="font-bold text-emerald-700">RM {(staff.net_pay || 0).toLocaleString()}</span></div>
            </div>
          </Section>

          <div className="lg:col-span-2">
            <Section title="Sejarah Kontrak" testid="contract-history">
              <SimpleTable
                columns={[
                  { key: 'contract_type', label: 'Jenis' },
                  { key: 'start_date', label: 'Tarikh Mula' },
                  { key: 'end_date', label: 'Tarikh Tamat' },
                  { key: 'probation_status', label: 'Probation' },
                  { key: 'salary', label: 'Gaji' },
                ]}
                rows={contract.history}
                emptyMsg="Tiada sejarah kontrak"
                renderCell={(k, r) => k === 'salary' ? `RM ${(r.salary || 0).toLocaleString()}` : (r[k] || '—')}
              />
            </Section>
          </div>
        </div>
      )}

      {activeTab === 'attendance' && (
        <div className="space-y-5" data-testid="tab-attendance">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Hadir" value={attendance.present} tone="emerald" />
            <Stat label="Lewat" value={attendance.late} tone="amber" />
            <Stat label="Tidak Hadir" value={attendance.absent} tone="red" />
            <Stat label="Cuti" value={attendance.leave} tone="purple" />
          </div>
          <Section title="Rekod Kehadiran Terkini">
            <SimpleTable
              columns={[
                { key: 'date', label: 'Tarikh' },
                { key: 'status', label: 'Status' },
                { key: 'check_in', label: 'Masuk' },
                { key: 'check_out', label: 'Keluar' },
                { key: 'late_minutes', label: 'Lewat (min)' },
                { key: 'notes', label: 'Nota' },
              ]}
              rows={attendance.recent}
              renderCell={(k, r) => k === 'status' ? <StatusPill status={r.status} /> : (r[k] || '—')}
            />
          </Section>
        </div>
      )}

      {activeTab === 'performance' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="tab-performance">
          <Section title="Review Prestasi">
            {performance.list?.length ? (
              <div className="space-y-3">
                {performance.list.map((p) => (
                  <div key={p.id} className="border border-gray-100 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-gray-800">{p.period}</p>
                        <p className="text-xs text-gray-500">oleh {p.manager_comments ? 'Manager' : '—'}</p>
                      </div>
                      <span className="text-2xl font-bold text-blue-600">{p.overall_score || p.rating || '—'}</span>
                    </div>
                    {p.strengths && <p className="text-xs text-gray-600 mt-2"><b className="text-emerald-700">Kekuatan:</b> {p.strengths}</p>}
                    {p.improvements && <p className="text-xs text-gray-600 mt-1"><b className="text-amber-700">Penambahbaikan:</b> {p.improvements}</p>}
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-gray-400 py-4 text-center">Tiada review</p>}
          </Section>

          <Section title="KPI Staff">
            <SimpleTable
              columns={[
                { key: 'kpi_name', label: 'KPI' },
                { key: 'period', label: 'Tempoh' },
                { key: 'target_value', label: 'Sasaran' },
                { key: 'actual_value', label: 'Sebenar' },
                { key: 'status', label: 'Status' },
              ]}
              rows={kpi.list}
              emptyMsg="Tiada KPI direkod"
              renderCell={(k, r) => k === 'status' ? <StatusPill status={r.status} /> : (r[k] ?? '—')}
            />
          </Section>
        </div>
      )}

      {activeTab === 'training' && (
        <div data-testid="tab-training">
          <Section title="Rekod Latihan & Pembangunan">
            <SimpleTable
              columns={[
                { key: 'training_name', label: 'Latihan' },
                { key: 'training_type', label: 'Jenis' },
                { key: 'provider', label: 'Penyedia' },
                { key: 'start_date', label: 'Mula' },
                { key: 'end_date', label: 'Tamat' },
                { key: 'status', label: 'Status' },
                { key: 'cost', label: 'Kos' },
                { key: 'certificate_obtained', label: 'Sijil' },
              ]}
              rows={training.list}
              renderCell={(k, r) => {
                if (k === 'status') return <StatusPill status={r.status} />;
                if (k === 'cost') return `RM ${(r.cost || 0).toLocaleString()}`;
                if (k === 'certificate_obtained') return r.certificate_obtained ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : '—';
                return r[k] || '—';
              }}
            />
          </Section>
        </div>
      )}

      {activeTab === 'leave' && (
        <div data-testid="tab-leave">
          <Section title="Sejarah Cuti">
            <SimpleTable
              columns={[
                { key: 'leave_type', label: 'Jenis' },
                { key: 'start_date', label: 'Mula' },
                { key: 'end_date', label: 'Tamat' },
                { key: 'days', label: 'Hari' },
                { key: 'reason', label: 'Sebab' },
                { key: 'status', label: 'Status' },
              ]}
              rows={leave.list}
              renderCell={(k, r) => k === 'status' ? <StatusPill status={r.status} /> : (r[k] ?? '—')}
            />
          </Section>
        </div>
      )}

      {activeTab === 'conduct' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="tab-conduct">
          <Section title="Tindakan Disiplin">
            {disciplinary.list?.length ? (
              <div className="space-y-3">
                {disciplinary.list.map((d) => (
                  <div key={d.id} className="border border-red-100 bg-red-50 rounded-lg p-3">
                    <div className="flex justify-between">
                      <span className="font-semibold text-red-800">{d.type}</span>
                      <span className="text-xs text-gray-500">{d.date}</span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">{d.reason}</p>
                    {d.action_taken && <p className="text-xs text-gray-600 mt-1"><b>Tindakan:</b> {d.action_taken}</p>}
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-gray-400 py-4 text-center">Tiada tindakan disiplin</p>}
          </Section>

          <Section title="Ganjaran & Insentif">
            {rewards.list?.length ? (
              <div className="space-y-3">
                {rewards.list.map((r) => (
                  <div key={r.id} className="border border-emerald-100 bg-emerald-50 rounded-lg p-3">
                    <div className="flex justify-between">
                      <span className="font-semibold text-emerald-800 flex items-center gap-1.5"><Gift className="w-4 h-4" />{r.title || r.reward_type}</span>
                      <span className="text-sm font-bold text-emerald-700">RM {(r.value || 0).toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{r.description}</p>
                    <p className="text-xs text-gray-500 mt-1">{r.date} • oleh {r.awarded_by || '—'}</p>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-gray-400 py-4 text-center">Tiada ganjaran</p>}
          </Section>
        </div>
      )}

      {activeTab === 'skills' && (
        <div data-testid="tab-skills">
          <Section title="Kemahiran & Pensijilan">
            <SimpleTable
              columns={[
                { key: 'skill_name', label: 'Kemahiran' },
                { key: 'category', label: 'Kategori' },
                { key: 'current_level', label: 'Tahap Sekarang' },
                { key: 'required_level', label: 'Tahap Diperlukan' },
                { key: 'certified', label: 'Bertauliah' },
                { key: 'certification_name', label: 'Pensijilan' },
                { key: 'expiry_date', label: 'Tamat' },
              ]}
              rows={skills.list}
              renderCell={(k, r) => k === 'certified' ? (r.certified ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : '—') : (r[k] || '—')}
            />
          </Section>
        </div>
      )}

      {activeTab === 'documents' && (
        <div data-testid="tab-documents">
          <Section title={`Dokumen Staff (${documents.total})`}>
            {documents.list?.length ? (
              <ul className="space-y-2">
                {documents.list.map((d, i) => (
                  <li key={i} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-5 h-5 text-gray-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 truncate">{d.name || d.filename || d.title}</p>
                        <p className="text-xs text-gray-500">{d.category || d.type} • {d.uploaded_at || d.created_at}</p>
                      </div>
                    </div>
                    {d.size && <span className="text-xs text-gray-400">{(d.size / 1024).toFixed(1)} KB</span>}
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-gray-400 py-4 text-center">Tiada dokumen dimuatnaik</p>}
          </Section>
        </div>
      )}
    </div>
  );
};

export default StaffProfile360Page;
